import { useQuery } from '@tanstack/react-query'
import { BUILD } from '../lib/build.ts'
import { readPlatformIdentity } from '../lib/gateway.ts'
import { SESSION_QUERY_KEY } from '../shell/app-shell.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { useServiceHealth } from '../shell/environment.ts'
import { EnvironmentPill } from '../shell/environment-badge.tsx'
import { Card } from '../tenants/card.tsx'
import { CopyButton } from '../tenants/copy-button.tsx'
import { formatInstantWithoutZone, ownerTimeZone } from '../tenants/instants.ts'
import { ThemeChoice } from '../theme/theme-controls.tsx'

/**
 * Your account — §4.4's last row, as far as the API allows today.
 *
 * It shows who is signed in, from the same read the shell already made: the nickname and
 * the full name, as the database has them **now** rather than as a token claimed them.
 *
 * **Change my password is not here, and that is a gap rather than a decision.** §4.4 and
 * §22 put "a platform admin changing their own password, current password required,
 * ending every other session for that account" on **A03**, and A03 shipped the realm
 * without it: there is no such operation in the OpenAPI document, so there is nothing for
 * this screen to call. Until it exists, rotating the only platform password is the same
 * operations path a locked-out owner uses (§4.5). Said on the screen rather than left as
 * a control that does nothing.
 *
 * **P07 (2026-10-14) made it three cards** (You, Password, This deployment) and gave the deployment
 * card the API's own build beside the console's. The console's commit is shown in full with a Copy
 * control, its build time in the **owner's** zone with the zone named (§7.5, a platform record), and
 * the API's `build` comes from `GET /health` — the read the masthead already makes for the
 * environment word, observed through the same query, so this page asks for nothing new.
 *
 * **P09 (2026-10-15) added the Theme card** — System, Light or Dark, kept in this browser
 * (`theme/`). The masthead has a second, three-icon control for the same preference.
 *
 * **"This deployment"** is this task's own addition (`chore-console-build-identifier`): the
 * commit and build time `lib/build.ts` read from what Vite baked into this bundle, so a QA
 * report about this screen can also cite which commit it tested — the same reason
 * `apps/api`'s `GET /health` carries a `build` object. §5.1 rules out a footer for this, so
 * it sits here instead, beside the other read-only facts about this browser's session.
 */
/** A build's commit in full, monospace and copyable — or the word for why there is none. */
function CommitValue({ commit, noun }: { readonly commit: string | null; readonly noun: string }) {
  if (commit === null) {
    return <span className="muted">—</span>
  }
  return (
    <span className="row row-tight">
      <code className="wrap">{commit}</code>
      <CopyButton value={commit} noun={noun} quiet />
    </span>
  )
}

/** An instant in the owner's zone, with the zone named beside it, or a dash. */
function BuildInstant({ iso, zone }: { readonly iso: string | null; readonly zone: string }) {
  const formatted = formatInstantWithoutZone(iso, zone)
  if (formatted === '—') {
    return <span className="muted">—</span>
  }
  return (
    <>
      {formatted} <code className="muted">{zone}</code>
    </>
  )
}

export function AccountScreen() {
  useDocumentTitle('Your account')
  const identity = useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: readPlatformIdentity })
  const health = useServiceHealth()
  const zone = ownerTimeZone()
  const api = health.data?.build

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <div>
          <h1>Your account</h1>
          <p className="page-lead">
            The administrator this browser is signed in as, as the database has them now.
          </p>
        </div>
      </div>

      <Card title="You">
        <div className="card-body">
          <dl className="kv">
            <dt>Nickname</dt>
            <dd className="mono">{identity.data?.nickname ?? '—'}</dd>
            <dt>Full name</dt>
            <dd>{identity.data?.fullName ?? '—'}</dd>
          </dl>
        </div>
      </Card>

      <Card title="Theme">
        <div className="card-body">
          <ThemeChoice />
        </div>
      </Card>

      <Card title="Password">
        <div className="card-body">
          <p className="small secondary">
            The API has no operation for changing a platform administrator’s password yet, so this
            console cannot offer one. Until it does, a password is changed against the deployment by
            whoever holds the Wrangler credentials — the same way the first administrator was
            created.
          </p>
        </div>
      </Card>

      <Card
        title="This deployment"
        description="What this console and the API it talks to were built from."
        action={<EnvironmentPill />}
      >
        <div className="card-body">
          <dl className="kv">
            <dt>Console commit</dt>
            <dd>
              <CommitValue commit={BUILD.commit} noun="console commit" />
            </dd>
            <dt>Console built</dt>
            <dd>
              <BuildInstant iso={BUILD.time} zone={zone} />
            </dd>
            <dt>API commit</dt>
            <dd>
              <CommitValue commit={api?.commit ?? null} noun="API commit" />
            </dd>
            <dt>API release</dt>
            <dd>
              {api === undefined ? (
                <span className="muted">—</span>
              ) : (
                (api.version ?? <span className="muted">Not a release</span>)
              )}
            </dd>
            <dt>API deployed</dt>
            <dd>
              <BuildInstant iso={api?.deployedAt ?? null} zone={zone} />
            </dd>
          </dl>
          {health.isError && api === undefined ? (
            <p className="small muted" role="status">
              The API’s build could not be read just now. It is asked again in the background.
            </p>
          ) : null}
        </div>
      </Card>
    </main>
  )
}
